import { useCallback, useRef, useState, type ReactNode } from "react";
import { useDashLang } from "@/context/DashboardLangContext";
import { useDialog } from "@/components/ui/useDialog";
import Button from "@/components/ui/Button";
import { ConfirmContext, type ConfirmFn, type ConfirmOptions } from "@/components/ui/confirm";

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    resolver.current?.(false); // a newer prompt replaces an unanswered one
    // A bare string is the window.confirm() shape, used for deletes and
    // cancellations — treat it as destructive.
    setPending(typeof opts === "string" ? { message: opts, danger: true } : opts);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  const settle = (ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmModal opts={pending} onSettle={settle} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmModal({ opts, onSettle }: { opts: ConfirmOptions; onSettle: (ok: boolean) => void }) {
  const { isAr, t } = useDashLang();
  const dialogRef = useDialog(() => onSettle(false));
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => onSettle(false)} dir={isAr ? "rtl" : "ltr"}>
      <div ref={dialogRef} role="alertdialog" aria-modal="true" tabIndex={-1}
        aria-labelledby="confirm-title" aria-describedby="confirm-message"
        className="bg-white rounded-2xl w-full max-w-sm shadow-xl p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${opts.danger ? "bg-red-50 text-red-600" : "bg-gold-soft text-gold-ink"}`}>
            <i className={opts.danger ? "ri-error-warning-line text-lg" : "ri-question-line text-lg"} aria-hidden="true"></i>
          </div>
          <div className="min-w-0">
            <h2 id="confirm-title" className={`font-semibold text-ink ${isAr ? "font-tajawal" : ""}`}>
              {opts.title ?? t("Are you sure?", "هل أنت متأكد؟")}
            </h2>
            <p id="confirm-message" className={`text-sm text-muted mt-1 ${isAr ? "font-tajawal" : ""}`}>{opts.message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" arFont={isAr} onClick={() => onSettle(false)}>
            {t("Keep it", "تراجع")}
          </Button>
          <Button variant={opts.danger ? "danger" : "primary"} arFont={isAr} onClick={() => onSettle(true)}>
            {opts.confirmLabel ?? t("Confirm", "تأكيد")}
          </Button>
        </div>
      </div>
    </div>
  );
}
